import { createFilterOptions, ListSubheader, Typography } from '@mui/material';
import { getTaxExemptStatus } from 'src/utils/einStatus';
import {
  capRecipientOptions,
  getRecipientMatchGroupLabel,
  getRecipientMatchLabels,
  getRecipientMatchNote,
  getRecipientMoreHint,
} from 'src/utils/foodRunRecipientMatching';

// Autocomplete renderGroup for food run receivers: the stock sticky header,
// plus an optional note under one group's header (Best matches).
export const renderRecipientMatchGroup = ({ noteGroup, note }) =>
  function RecipientMatchGroup(params) {
    return (
      <li key={params.key}>
        <ListSubheader component="div" sx={{ top: -8 }}>
          {params.group}
        </ListSubheader>
        {note && params.group === noteGroup ? (
          <Typography
            component="div"
            variant="caption"
            color="text.secondary"
            sx={{ px: 2, pb: 1 }}
          >
            {note}
          </Typography>
        ) : null}
        <ul style={{ padding: 0 }}>{params.children}</ul>
      </li>
    );
  };

// The caption lines under a receiver option, from getRecipientMatchLabels.
export function RecipientMatchCaption({ labels }) {
  if (!labels) return null;
  const { distance, reasons, warnings } = labels;

  return (
    <>
      {distance || reasons ? (
        <Typography variant="caption" alignSelf="flex-start">
          {distance ? (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
            >
              {distance}
            </Typography>
          ) : null}
          {distance && reasons ? ' · ' : null}
          {reasons ? (
            <Typography component="span" variant="caption" color="success.dark">
              {reasons}
            </Typography>
          ) : null}
        </Typography>
      ) : null}
      {warnings ? (
        <Typography
          color="warning.main"
          variant="caption"
          alignSelf="flex-start"
        >
          ⚠ {warnings}
        </Typography>
      ) : null}
    </>
  );
}

// Non-selectable last row telling the user how many rows are hidden.
export function RecipientMoreHint({ label, ...props }) {
  return (
    <Typography
      component="li"
      {...props}
      variant="caption"
      color="text.secondary"
      sx={{ px: 2, py: 1.5, fontStyle: 'italic' }}
    >
      {label}
    </Typography>
  );
}

const filterRecipientItems = createFilterOptions({
  stringify: (option) => option.title || '',
});

// Props for AutoCompleteComponent (src/hooks/useForm) on the food run edit
// screens: grouped items with match captions, the sparse-profile note, the
// capped "other" list, and the selected receiver's warnings as helper text.
export const getFoodRunRecipientAutocompleteProps = ({
  recipients,
  value,
  t,
}) => {
  const bestLabel = getRecipientMatchGroupLabel(
    { recipient_match_group: 'best' },
    t
  );
  const otherLabel = getRecipientMatchGroupLabel(
    { recipient_match_group: 'other' },
    t
  );
  const selectedWarnings = getRecipientMatchLabels(
    recipients.find(({ team_id }) => team_id === value),
    t
  ).warnings;

  return {
    items: recipients.map((recipient) => ({
      value: recipient.team_id,
      title: recipient.name,
      formatted_address: recipient.formatted_address || recipient.email,
      animal_food_rescuer_type: recipient.animal_food_rescuer_type,
      tax_exempt_status: getTaxExemptStatus(recipient),
      group: getRecipientMatchGroupLabel(recipient, t),
      recipient_match_group: recipient.recipient_match_group,
      disabled: recipient.onboarding?.isPending === true,
      pending_label: recipient.onboarding?.isPending
        ? t('donations.pending-onboarding-status')
        : '',
      match_labels: getRecipientMatchLabels(recipient, t),
    })),
    groupBy: (option) => option.group,
    renderGroup: renderRecipientMatchGroup({
      noteGroup: bestLabel,
      note: getRecipientMatchNote(recipients, t),
    }),
    filterOptions: (options, state) =>
      capRecipientOptions(filterRecipientItems(options, state), {
        getGroup: (option) => option.recipient_match_group,
        makeHint: (count) => ({
          value: '__more__',
          title: '',
          group: otherLabel,
          recipient_match_group: 'other',
          disabled: true,
          more_hint: getRecipientMoreHint(count, t),
        }),
      }),
    helperText: selectedWarnings ? `⚠ ${selectedWarnings}` : '',
    helperTextColor: 'warning.main',
  };
};
